import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import LoginPrompt from '../../Auth/LoginPrompt';
import './SchoolChallengeEventPage.css';

const SchoolChallengeEventPage = () => {
  const { currentUser, loading } = useAuth();
  const iframeRef = useRef(null);
  
  // 최신 상태를 항상 참조하기 위한 ref (closure 문제 해결)
  const userRef = useRef(currentUser);
  const loadingRef = useRef(loading);
  
  // 로그인 모달 상태
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  
  // iframe 로드 상태
  const [iframeLoaded, setIframeLoaded] = useState(false);
  
  // 대기 중인 인증 요청 (Auth 로딩 완료 후 처리)
  const [pendingAuthRequest, setPendingAuthRequest] = useState(false);
  
  // 상태가 변경될 때마다 ref 업데이트
  useEffect(() => {
    userRef.current = currentUser;
    loadingRef.current = loading;
  }, [currentUser, loading]);
  
  // Auth 로딩 완료 후 대기 중인 요청 처리
  useEffect(() => {
    if (!loading && pendingAuthRequest) {
      setPendingAuthRequest(false);
      if (currentUser) {
        // 로그인됨 → 인증 정보 전달
        setTimeout(() => {
          if (iframeRef.current && iframeRef.current.contentWindow) {
            const message = {
              type: 'AUTH_SUCCESS',
              user: {
                uid: currentUser.uid,
                email: currentUser.email,
                displayName: currentUser.displayName || currentUser.email?.split('@')[0] || '사용자'
              },
              timestamp: Date.now(),
              source: 'whatquiz'
            };
            iframeRef.current.contentWindow.postMessage(message, 'https://whatquiz-eventpage.web.app');
          }
        }, 100);
      } else {
        // 비로그인 → 로그인 모달 표시
        setShowLoginPrompt(true);
      }
    }
  }, [loading, pendingAuthRequest, currentUser]);

  // 이벤트 페이지 URL
  const EVENT_PAGE_URL = 'https://whatquiz-eventpage.web.app/event';
  const EVENT_PAGE_ORIGIN = 'https://whatquiz-eventpage.web.app';

  // iframe에 인증 정보 전달 (크로스 플랫폼 호환)
  const sendAuthToIframe = useCallback(() => {
    try {
      if (iframeRef.current && iframeRef.current.contentWindow && currentUser && iframeLoaded) {
        const message = {
          type: 'AUTH_SUCCESS',
          user: {
            uid: currentUser.uid,
            email: currentUser.email,
            displayName: currentUser.displayName || currentUser.email?.split('@')[0] || '사용자'
          },
          // 추가 메타데이터 (디버깅용)
          timestamp: Date.now(),
          source: 'whatquiz'
        };
        
        iframeRef.current.contentWindow.postMessage(message, EVENT_PAGE_ORIGIN);
      }
    } catch (error) {
      console.warn('iframe 메시지 전송 실패:', error);
    }
  }, [currentUser, iframeLoaded]);

  // iframe으로부터 메시지 수신 (크로스 플랫폼 호환)
  useEffect(() => {
    const handleMessage = (event) => {
      // 보안: 이벤트 페이지에서 온 메시지만 처리
      if (!event.origin || event.origin !== EVENT_PAGE_ORIGIN) return;
      
      // 데이터 유효성 검사
      if (!event.data || typeof event.data !== 'object') return;
      
      if (event.data.type === 'REQUEST_AUTH') {
        // Auth가 아직 로딩 중이면 대기
        if (loadingRef.current) {
          setPendingAuthRequest(true);
          return;
        }
        
        // 이벤트 페이지에서 인증 요청
        // userRef.current를 사용하여 항상 최신 user 상태 참조 (closure 문제 해결)
        if (userRef.current) {
          // 이미 로그인됨 → 바로 정보 전달
          sendAuthToIframe();
        } else {
          // 로그인 모달 표시
          setShowLoginPrompt(true);
        }
      }
    };
    
    // 이벤트 리스너 등록 (dependency 최소화로 리스너 재등록 방지)
    window.addEventListener('message', handleMessage, false);
    
    return () => {
      window.removeEventListener('message', handleMessage, false);
    };
  }, [sendAuthToIframe]); // ref로 참조하므로 dependency 최소화

  // 유저 상태 변경 시 iframe에 알림
  useEffect(() => {
    if (currentUser && iframeLoaded) {
      // 딜레이를 주어 안정성 확보 (특히 모바일)
      const timer = setTimeout(() => {
        sendAuthToIframe();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [currentUser, iframeLoaded, sendAuthToIframe]);

  // iframe 로드 완료 핸들러
  const handleIframeLoad = () => {
    setIframeLoaded(true);
    // 이미 로그인되어 있으면 인증 정보 전달
    if (currentUser) {
      // iOS/Android에서 iframe 로드 후 약간의 지연 필요
      setTimeout(() => {
        sendAuthToIframe();
      }, 500);
    }
  };

  return (
    <div className="school-challenge-event-page">
      <Helmet>
        <title>학교 대항전 - 왓퀴즈</title>
        <meta name="description" content="우리 학교가 1등! 학교별로 참여하여 특별한 혜택을 받아보세요." />
        <meta property="og:title" content="학교 대항전 - 왓퀴즈" />
        <meta property="og:description" content="우리 학교가 1등! 학교별로 참여하여 특별한 혜택을 받아보세요." />
      </Helmet>

      {/* iframe 컨테이너 */}
      <div className="school-challenge-iframe-container">
        {!iframeLoaded && (
          <div className="school-challenge-loading">
            <div className="school-challenge-loading-spinner"></div>
            <p>이벤트 페이지를 불러오는 중...</p>
          </div>
        )}
        <iframe
          ref={iframeRef}
          src={EVENT_PAGE_URL}
          className={`school-challenge-iframe ${iframeLoaded ? 'loaded' : ''}`}
          title="학교 대항전 이벤트"
          onLoad={handleIframeLoad}
          allow="clipboard-write"
        />
      </div>

      {/* 로그인 프롬프트 모달 */}
      <LoginPrompt
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        feature="default"
      />
    </div>
  );
};

export default SchoolChallengeEventPage;
